import { useState, useMemo, useEffect } from 'react'
import { useStableMount } from '../../hooks/useStableMount'
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Divider,
  useMediaQuery,
  useTheme,
  Badge,
  Chip,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useClients } from '../../hooks/useClients'
import DashboardIcon from '@mui/icons-material/Dashboard'
import PeopleIcon from '@mui/icons-material/People'
import CampaignIcon from '@mui/icons-material/Campaign'
import BarChartIcon from '@mui/icons-material/BarChart'
import PaymentIcon from '@mui/icons-material/Payment'
import SettingsIcon from '@mui/icons-material/Settings'
import UploadIcon from '@mui/icons-material/Upload'
import BusinessIcon from '@mui/icons-material/Business'
import StorefrontIcon from '@mui/icons-material/Storefront'
import GroupsIcon from '@mui/icons-material/Groups'
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts'
import StorageIcon from '@mui/icons-material/Storage'
import AssignmentIcon from '@mui/icons-material/Assignment'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import ApprovalIcon from '@mui/icons-material/Approval'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import LogoutIcon from '@mui/icons-material/Logout'
import { getUserFullName, getUserInitials, getAvatarGradient } from '../../lib/userUtils'
import { getApiAssetUrl } from '../../lib/api'
import { useInfluencers } from '../../hooks/useInfluencers'
import { Logo } from '../common/Logo'
import { ConditionalTooltip } from '../common/ConditionalTooltip'
import { getRoleDisplayLabel, isAccountManager, isSuperAdmin } from '../../lib/roles'
import { getInfluencerNavDescription } from '../../lib/influencerNavCopy'

export const SIDEBAR_WIDTH = 280
export const SIDEBAR_COLLAPSED_WIDTH = 80

// Prefetch route chunks on hover so navigation feels instant (same paths as App.jsx lazy imports)
const prefetchByPath = {
  '/admin': () => import('../dashboard/AdminDashboard'),
  '/admin/clients': () => import('../clients/ClientList'),
  '/admin/campaigns': () => import('../campaigns/CampaignList'),
  '/admin/influencers': () => import('../influencers/InfluencerManagementPage'),
  '/admin/analytics': () => import('../analytics/AnalyticsPage'),
  '/admin/budget-tracker': () => import('../budget/AdminBudgetTrackerPage'),
  '/admin/payments': () => import('../payments/PaymentsPage'),
  '/admin/content-vault': () => import('../contentVault/ContentVaultPage'),
  '/admin/profile': () => import('../profile/ProfilePage'),
  '/admin/settings': () => import('../settings/SettingsPage'),
  '/admin/tenants': () => import('../tenants/TenantsPage'),
  '/admin/platform-admins': () => import('../superAdmins/SuperAdminsPage'),
  '/super-admin': () => import('../tenants/TenantsPage'),
  '/super-admin/tenants': () => import('../tenants/TenantsPage'),
  '/super-admin/super-admins': () => import('../superAdmins/SuperAdminsPage'),
  '/super-admin/platform-admins': () => import('../superAdmins/SuperAdminsPage'),
  '/super-admin/people': () => import('../platformDirectory/PeopleDirectoryPage'),
  '/super-admin/data-hygiene': () => import('../platformDirectory/DataHygienePage'),
  '/super-admin/platform/storage-usage': () => import('../platformDirectory/PlatformStorageUsagePage'),
  '/super-admin/platform/tenant-costs': () => import('../platformDirectory/PlatformTenantCostPage'),
  '/super-admin/profile': () => import('../profile/ProfilePage'),
  '/super-admin/settings': () => import('../settings/SettingsPage'),
  '/client': () => import('../dashboard/ClientDashboard'),
  '/client/approvals': () => import('../approvals/ApprovalsQueuePage'),
  '/client/campaigns': () => import('../campaigns/CampaignList'),
  '/client/content-vault': () => import('../contentVault/ContentVaultPage'),
  '/client/profile': () => import('../profile/ProfilePage'),
  '/client/settings': () => import('../settings/SettingsPage'),
  '/influencer': () => import('../dashboard/InfluencerDashboard'),
  '/influencer/tasks': () => import('../deliverables/MyTasksPage'),
  '/influencer/campaigns': () => import('../campaigns/CampaignList'),
  '/influencer/submissions': () => import('../deliverables/MySubmissionsPage'),
  '/influencer/profile': () => import('../profile/ProfilePage'),
  '/influencer/settings': () => import('../settings/SettingsPage'),
}
const prefetchedPaths = new Set()
const CONTENT_VAULT_ENABLED = String(import.meta.env.VITE_CONTENT_VAULT_ENABLED || 'true').toLowerCase() !== 'false'
const prefetchRoute = (path) => {
  if (prefetchedPaths.has(path)) return
  const fn = prefetchByPath[path]
  if (fn) {
    prefetchedPaths.add(path)
    fn()
  }
}

/**
 * Sidebar navigation component for Admin and Client users
 * Features:
 * - Collapsible sidebar
 * - Role-based navigation items
 * - User profile section
 * - Active state highlighting
 * - Mobile responsive (drawer)
 */
export const Sidebar = ({ mobileOpen, onMobileClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userProfile, signOut, isViewingAsUser } = useAuth()
  const isSuperAdminNav = isSuperAdmin(userProfile) && !isViewingAsUser
  const adminBasePath = isSuperAdminNav ? '/super-admin' : '/admin'
  const stable = useStableMount()

  // Prefetch analytics chunk for admin so navigation to Analytics is fast
  useEffect(() => {
    if (isAccountManager(userProfile) || isSuperAdmin(userProfile)) {
      import('../analytics/AnalyticsPage')
    }
  }, [userProfile])

  useClients({ enabled: stable })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [collapsed, setCollapsed] = useState(false)
  /** Full-width drawer on mobile should always show labels + wordmark; `collapsed` only applies to the desktop rail. */
  const sidebarCompact = isMobile ? false : collapsed
  const expandedLogoMaxWidth = SIDEBAR_WIDTH - 88

  // Get pending invitations count for influencers
  const { influencers: invitations } = useInfluencers(null, { enabled: stable })
  useMemo(() => {
    if (userProfile?.role !== 'influencer') return 0
    return (invitations || []).filter(inv => inv.status === 'invited').length
  }, [userProfile?.role, invitations])

  // Define navigation items based on user role
  const navigationItems = useMemo(() => {
    if (!userProfile) return []

    const baseItems = [
      {
        text: 'Dashboard',
        icon: <DashboardIcon />,
        path: `/${userProfile.role}`,
      },
    ]

    if (isAccountManager(userProfile) || isSuperAdmin(userProfile)) {
      // Super-admin with no tenant selected: Tenants + Super admins
      if (isSuperAdminNav) {
        return [
          {
            text: 'Tenants',
            icon: <BusinessIcon />,
            path: '/super-admin/tenants',
          },
          {
            text: 'Super admins',
            icon: <PeopleIcon />,
            path: '/super-admin/super-admins',
          },
          {
            text: 'People Directory',
            icon: <ManageAccountsIcon />,
            path: '/super-admin/people',
          },
          {
            text: 'Data hygiene',
            icon: <CleaningServicesIcon />,
            path: '/super-admin/data-hygiene',
          },
          {
            text: 'Storage activity',
            icon: <StorageIcon />,
            path: '/super-admin/platform/storage-usage',
          },
          {
            text: 'Cost estimates',
            icon: <PaymentIcon />,
            path: '/super-admin/platform/tenant-costs',
          },
        ]
      }
      // Tenant admin (or super-admin acting as a tenant): full admin nav
      return [
        {
          text: 'Dashboard',
          icon: <DashboardIcon />,
          path: adminBasePath,
        },
        {
          text: 'Clients',
          icon: <PeopleIcon />,
          path: '/admin/clients',
        },
        {
          text: 'Campaigns',
          icon: <CampaignIcon />,
          path: '/admin/campaigns',
        },
        {
          text: 'Influencers',
          icon: <PeopleIcon />,
          path: '/admin/influencers',
        },
        {
          text: 'Analytics',
          icon: <BarChartIcon />,
          path: '/admin/analytics',
        },
        ...(isAccountManager(userProfile)
          ? [
              {
                text: 'Budget Tracker',
                icon: <PaymentIcon />,
                path: '/admin/budget-tracker',
              },
              {
                text: 'Billing',
                icon: <PaymentIcon />,
                path: '/admin/payments',
              },
            ]
          : []),
        ...(CONTENT_VAULT_ENABLED
          ? [{
              text: 'Content Vault',
              beta: true,
              icon: <CampaignIcon />,
              path: '/admin/content-vault',
            }]
          : []),
      ]
    }

    if (userProfile.role === 'client') {
      return [
        ...baseItems,
        {
          text: 'Approvals',
          icon: <ApprovalIcon />,
          path: '/client/approvals',
        },
        {
          text: 'My Campaigns',
          icon: <CampaignIcon />,
          path: '/client/campaigns',
        },
        ...(CONTENT_VAULT_ENABLED
          ? [{
              text: 'Content Vault',
              beta: true,
              icon: <StorageIcon />,
              path: '/client/content-vault',
            }]
          : []),
      ]
    }

    if (userProfile.role === 'influencer') {
      return [
        ...baseItems,
        {
          text: 'My Tasks',
          icon: <AssignmentIcon />,
          path: '/influencer/tasks',
        },
        {
          text: 'My Campaigns',
          icon: <CampaignIcon />,
          path: '/influencer/campaigns',
        },
        {
          text: 'My Submissions',
          icon: <UploadIcon />,
          path: '/influencer/submissions',
        },
      ]
    }

    return baseItems
  }, [userProfile, isSuperAdminNav, adminBasePath])

  const roleLabel = useMemo(() => {
    if (!userProfile?.role) return ''
    if (isSuperAdmin(userProfile)) return 'Super Admin'
    if (isAccountManager(userProfile)) return 'Account Manager'
    return getRoleDisplayLabel(userProfile) || userProfile.role
  }, [userProfile])

  const isActive = (path) => {
    // Keep the top-level "Dashboard" item from staying highlighted on every deeper admin route.
    if (['/admin', '/client', '/influencer'].includes(path)) return location.pathname === path
    return location.pathname === path || location.pathname.startsWith(`${path}/`)
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'secondary.main',
        color: 'secondary.contrastText',
      }}
    >
      {/* Logo Section: single clear focal point — tenant logo or Social Box; when collapsed, logo above hamburger */}
      <Box
        sx={{
          py: 2.5,
          px: 2,
          display: 'flex',
          flexDirection: sidebarCompact ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: sidebarCompact ? 'center' : 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCompact ? 'center' : 'flex-start',
            flex: sidebarCompact ? undefined : 1,
            minWidth: 0,
            flexShrink: sidebarCompact ? undefined : 0,
          }}
        >
          <Logo
            variant="white"
            height={sidebarCompact ? 36 : 44}
            sx={{ maxWidth: sidebarCompact ? 36 : expandedLogoMaxWidth }}
          />
        </Box>
        {!isMobile && (
          <IconButton
            onClick={() => setCollapsed(!collapsed)}
            size="small"
            sx={{ color: 'white', flexShrink: 0 }}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 2, py: 2 }} role="navigation" aria-label="Main navigation">
        {navigationItems.map((item) => {
          const influencerDescription =
            userProfile?.role === 'influencer' ? getInfluencerNavDescription(item.path) : ''
          const tooltipTitle = sidebarCompact
            ? [item.text, influencerDescription].filter(Boolean).join(' — ')
            : influencerDescription

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ConditionalTooltip title={tooltipTitle} placement="right" describeChild>
                <ListItemButton
                  role="link"
                  aria-label={`Navigate to ${item.text}`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  onMouseEnter={() => prefetchRoute(item.path)}
                  onClick={(event) => {
                    event.currentTarget?.blur?.()
                    navigate(item.path)
                    if (isMobile) onMobileClose()
                  }}
                  selected={isActive(item.path)}
                  sx={{
                    borderRadius: 2,
                    color: 'white',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                    },
                    justifyContent: sidebarCompact ? 'center' : 'flex-start',
                    px: sidebarCompact ? 1 : 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: 'white',
                      minWidth: sidebarCompact ? 0 : 40,
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCompact && (
                    <ListItemText
                      primary={item.beta ? (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          {item.text}
                          <Chip
                            label="Beta"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.625rem',
                              fontWeight: 600,
                              color: 'rgba(255,255,255,0.9)',
                              borderColor: 'rgba(255,255,255,0.35)',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                            variant="outlined"
                          />
                        </Box>
                      ) : item.text}
                      slotProps={{
                        primary: {
                          fontWeight: isActive(item.path) ? 600 : 400,
                          fontSize: '0.9375rem',
                          component: item.beta ? 'div' : 'span',
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </ConditionalTooltip>
            </ListItem>
          )
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* User Profile & Settings */}
      <Box sx={{ p: 2 }}>
        {/* Settings */}
        <ListItemButton
          onMouseEnter={() => prefetchRoute(userProfile?.role === 'admin' ? `${adminBasePath}/settings` : `/${userProfile.role}/settings`)}
          onClick={() => {
            navigate(userProfile?.role === 'admin' ? `${adminBasePath}/settings` : `/${userProfile.role}/settings`)
            if (isMobile) onMobileClose()
          }}
          sx={{
            borderRadius: 2,
            color: 'white',
            mb: 1,
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.08)',
            },
            justifyContent: sidebarCompact ? 'center' : 'flex-start',
            px: sidebarCompact ? 1 : 2,
          }}
        >
          <ListItemIcon
            sx={{
              color: 'white',
              minWidth: sidebarCompact ? 0 : 40,
              justifyContent: 'center',
            }}
          >
            <SettingsIcon />
          </ListItemIcon>
          {!sidebarCompact && <ListItemText primary="Settings" />}
        </ListItemButton>

        {/* User Profile */}
        <Box
          onMouseEnter={() => prefetchRoute(userProfile?.role === 'admin' ? `${adminBasePath}/profile` : `/${userProfile.role}/profile`)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: sidebarCompact ? 1 : 2,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.08)',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.12)',
            },
            justifyContent: sidebarCompact ? 'center' : 'flex-start',
          }}
          onClick={() => {
            navigate(userProfile?.role === 'admin' ? `${adminBasePath}/profile` : `/${userProfile.role}/profile`)
            if (isMobile) onMobileClose()
          }}
        >
          {userProfile.profile_pic_url ? (
            <Avatar
              src={getApiAssetUrl(userProfile.profile_pic_url)}
              alt={getUserFullName(userProfile)}
              sx={{ width: 40, height: 40 }}
            />
          ) : (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: getAvatarGradient(userProfile),
                fontWeight: 600,
              }}
            >
              {getUserInitials(userProfile)}
            </Avatar>
          )}
          {!sidebarCompact && (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'white',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {getUserFullName(userProfile)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  textTransform: 'capitalize',
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Logout Button */}
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: 'white',
            mt: 1,
            '&:hover': {
              bgcolor: 'rgba(239, 68, 68, 0.2)',
            },
            justifyContent: sidebarCompact ? 'center' : 'flex-start',
            px: sidebarCompact ? 1 : 2,
          }}
        >
          <ListItemIcon
            sx={{
              color: 'white',
              minWidth: sidebarCompact ? 0 : 40,
              justifyContent: 'center',
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          {!sidebarCompact && <ListItemText primary="Logout" />}
        </ListItemButton>
      </Box>
    </Box>
  )

  // Desktop: static sidebar, content area scrolls (parent has overflow: hidden)
  if (!isMobile) {
    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
    return (
      <Box
        component="nav"
        aria-label="Main navigation sidebar"
        sx={{
          width,
          flexShrink: 0,
          height: '100vh',
          overflow: 'hidden',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {drawerContent}
      </Box>
    )
  }

  // Mobile: overlay Drawer
  return (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={onMobileClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: SIDEBAR_WIDTH,
          boxSizing: 'border-box',
          borderRadius: 0,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}
